import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FILE_CONSTRAINTS } from "../Json/BulkUploadConfigJson";
import { BULK_UPLOAD_MESSAGES } from "../Json/BulkUploadMessages";

const { dropzone } = BULK_UPLOAD_MESSAGES;
const ACCEPT_ATTRIBUTE = FILE_CONSTRAINTS.acceptedExtensions.join(",");
const ACTIVATION_KEYS = ["Enter", " "];

/**
 * Drag-and-drop / click-to-browse picker.
 *
 * Presentational only: it reports the chosen file upwards and holds no
 * selection of its own, so the owning panel stays the single source of truth.
 */
const FileDropZone = ({ inputId, disabled, onFileSelected }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const [dropped] = event.dataTransfer?.files || [];
      if (dropped) onFileSelected(dropped);
    },
    [disabled, onFileSelected]
  );

  const handleChange = useCallback(
    (event) => {
      const [selected] = event.target.files || [];
      if (selected) onFileSelected(selected);
      // Reset so picking the same file again still raises a change event.
      event.target.value = "";
    },
    [onFileSelected]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!ACTIVATION_KEYS.includes(event.key)) return;
      event.preventDefault();
      openPicker();
    },
    [openPicker]
  );

  const stateClasses = isDragging
    ? "border-primary bg-primary-subtle"
    : "border-secondary-subtle bg-body-tertiary";

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={dropzone.ariaLabel}
      aria-disabled={disabled}
      className={`border border-2 rounded-3 text-center p-4 ${stateClasses} ${
        disabled ? "opacity-50 pe-none" : ""
      }`}
      style={{ borderStyle: "dashed", cursor: disabled ? "not-allowed" : "pointer" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={openPicker}
      onKeyDown={handleKeyDown}
    >
      <i className="ri-file-excel-2-line text-success fs-2 d-block mb-2" />

      <h6 className="mb-1">
        {dropzone.prompt} <span className="text-primary">{dropzone.browse}</span>
      </h6>

      <p className="text-muted font-size-12 mb-0">{dropzone.hint}</p>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        className="d-none"
        accept={ACCEPT_ATTRIBUTE}
        disabled={disabled}
        onChange={handleChange}
      />
    </div>
  );
};

FileDropZone.propTypes = {
  inputId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onFileSelected: PropTypes.func.isRequired,
};

FileDropZone.defaultProps = { disabled: false };

export default FileDropZone;
